import { isNil, keys, pickBy } from 'lodash';

import type { RosterAdultMember, RosterYouthMember } from '@appTypes/esb';
import type { OrgPositionDetail } from '@appTypes/esb/organizations/positions/_orgGuid';
import { YOUTH_MEMBER } from '@modules/advancement/packRoster/constants';

import {
  CelKey,
  DisplayPermissionPositions,
  PanelTitle,
  Positions,
} from '../constants';

export interface RosterAdultMemberExtended extends RosterAdultMember {
  /** NOTE: Positions from SB and Akela have different positionId values for the same position */
  /** RosterAdultMember has positions which can have Scout book positions */
  /** userOrgPositions has the list of positions that match assignable Akela positions */
  userOrgPositions: OrgPositionDetail[];
  /** registrationData has the list of positions from the registration info, this might have special positions
   * that grant individual feature permission like Calendar editor by sub-unit, etc
   */
  registrationData: {
    position: string;
    positionId: number;
    personPositionId: number;
    registrationExpireDt: string;
  }[];
  /** allPositions list of positions name combining Roster positions and registration positions */
  allPositions: string[];
  isExpired: boolean;
}

export interface RosterYouthMemberExtended extends RosterYouthMember {
  /** NOTE: Positions from SB and Akela have different positionId values for the same position */
  /** RosterAdultMember has positions which can have Scout book positions */
  /** userOrgPositions has the list of positions that match assignable Akela positions */
  userOrgPositions: OrgPositionDetail[];
  /** registrationData has the list of positions from the registration info, this might have special positions
   * that grant individual feature permission like Calendar editor by sub-unit, etc
   */
  registrationData: {
    position: string;
    positionId: number;
    personPositionId: number;
    registrationExpireDt: string;
  }[];
  /** allPositions list of positions name combining Roster positions and registration positions */
  allPositions: string[];
  isExpired: boolean;
}

export type PermsConfiguration = {
  isUnitRequired?: boolean;
  isUnitEnabled?: boolean;
  isSubRequired?: boolean;
  isSubEnabled?: boolean;
  isRecommended?: boolean;
  cannotAssign?: boolean;
};

// Unless is needed do not add flase rules, keep it simple
// Posiblre values:
// {isUnitRequired:true, isUnitEnabled:true, isSubRequired:true, isSubEnabled:true, isRecommended:true, cannotAssign:true}
export const PermsConfigurationObject = {
  'Chartered Organization Rep.': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { isUnitRequired: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Assistant Scoutmaster': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit Advancement Chair': {
    [CelKey.ACTIVITY_CREATE]: { isRecommended: true },

    [CelKey.ADVANCEMENT_AWARD]: { isRecommended: true },
    [CelKey.ADVANCEMENT_APROVE]: { isRecommended: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isRecommended: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Committee Chair': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  Scoutmaster: {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Council Unit Representative': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { isUnitRequired: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'COR/CUR Delegate': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { isUnitRequired: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Key 3 Delegate': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Sub-Unit Support': {
    [CelKey.ACTIVITY_CREATE]: { isSubRequired: true, isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isSubRequired: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  // Unit support doesn't exist yet
  'Unit Support': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Unit Merit Badge administrator': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Calendar Editor': {
    [CelKey.ACTIVITY_CREATE]: { isRecommended: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit Outdoors / Activities chair': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit Treasurer': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'New Member Coordinator': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit Chaplain': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit Religious Emblems Coord': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  // COMMITTEE SECRETARY is the same as Secretary
  // Secretary doesn't exist yet
  Secretary: {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit Training Chair': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Youth Protection Champion': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Youth Member': {
    [CelKey.ACTIVITY_CREATE]: { cannotAssign: true },

    [CelKey.ADVANCEMENT_AWARD]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_APROVE]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { cannotAssign: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitEnabled: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { cannotAssign: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Venturing Participant': {
    [CelKey.ACTIVITY_CREATE]: { cannotAssign: true },

    [CelKey.ADVANCEMENT_AWARD]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_APROVE]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { cannotAssign: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitEnabled: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { cannotAssign: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Assistant Cubmaster': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isRecommended: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isRecommended: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  Cubmaster: {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Den Leader': {
    [CelKey.ACTIVITY_CREATE]: { isSubRequired: true, isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isSubRequired: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { cannotAssign: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { isSubRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Den Support': {
    [CelKey.ACTIVITY_CREATE]: { isSubRequired: true, isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isSubRequired: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isSubRequired: true, isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { isSubRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Asst. Den Leader': {
    [CelKey.ACTIVITY_CREATE]: { cannotAssign: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isSubEnabled: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Committee Member': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isSubEnabled: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitEnabled: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit College Scouter Reserve': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isSubEnabled: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitEnabled: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit Scouter Reserve': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isSubEnabled: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitEnabled: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Venturing Crew Advisor': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  Skipper: {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Venturing Crew Assoc. Advisor': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitEnabled: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitEnabled: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  Mate: {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitEnabled: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitEnabled: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Digital Product Sale Manager ': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Venturing College Sctr Reserve': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isSubEnabled: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitEnabled: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Executive Officer': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { cannotAssign: true },

    [CelKey.CALENDAR_CREATE]: { isSubEnabled: true, isUnitEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitEnabled: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  // Pneding to know if they are going to be added here or not:
  // Explorer Post Advisor

  // Not selectable positions:----------
  // Assistant Webelos Leader
  // Pack Trainer
  // Lion Guide
  // Tiger Den Leader
  // Webelos Leader
  // Lion Adult Partner
  // Tiger Cub Adult

  // Positions that are not part of the registered/functional types (Mostly SB positions)

  'Scout Executive': {
    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { isUnitRequired: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Key 3': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Unit Key 3': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Unit Outdoors': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Activities Chair': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitEnabled: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Unit Admin': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
  },
  'Den Chief': {
    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
  },
  'Committee Secretary': {
    [CelKey.ACTIVITY_CREATE]: { isUnitEnabled: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { isUnitEnabled: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
  'Associate Crew Advisor': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
  },
  'Associate Sponsor': {
    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
  },
  'Post Associate Advisor': {
    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
  },
  'Post - Explorer Post Associate Advisor': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
  },
  'Explorer Post Admin': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
  },
  'Exploring Sponsor': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
  },
  'Post Committee Chairman': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },

  'Post Committee Chair': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Post Committee Member': {
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
  },
  'COR Delegate': {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { isUnitRequired: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  Advancement: {
    [CelKey.ACTIVITY_CREATE]: { isUnitRequired: true },

    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.CALENDAR_CREATE]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Registration Support': {
    [CelKey.ADVANCEMENT_AWARD]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_APROVE]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitRequired: true },

    [CelKey.FINANCE_CREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_INDIVCREATE]: { isUnitRequired: true },
    [CelKey.FINANCE_POCREATE]: { isUnitRequired: true },

    [CelKey.LEADERSHIP_EDIT]: { isUnitRequired: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { isUnitRequired: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { isUnitRequired: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { isUnitRequired: true },

    [CelKey.MESSAGING_CREATE]: { isUnitRequired: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitRequired: true },

    [CelKey.UNIT_PROFILE_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_EDIT]: { isUnitRequired: true },
    [CelKey.UNIT_REPORTS]: { isUnitRequired: true },
    [CelKey.SUBUNIT_CREATE]: { isUnitRequired: true },
    [CelKey.SUBUNIT_EDIT]: { isUnitRequired: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { isUnitRequired: true },
  },
  'Council Merit Badge Administrator': {
    [CelKey.ADVANCEMENT_MBLIST]: { isUnitRequired: true },
  },
  Youth: {
    [CelKey.ACTIVITY_CREATE]: { cannotAssign: true },

    [CelKey.ADVANCEMENT_AWARD]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_APROVE]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_EDIT]: { isUnitRequired: true },
    [CelKey.ADVANCEMENT_MBLIST]: { cannotAssign: true },
    [CelKey.ADVANCEMENT_SCOUT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_TENDERFOOT_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_SECONDCLASS_EDITOR]: { isUnitEnabled: true },
    [CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR]: { isUnitEnabled: true },

    [CelKey.CALENDAR_CREATE]: { isUnitEnabled: true, isSubEnabled: true },

    [CelKey.FINANCE_CREATE]: { cannotAssign: true },
    [CelKey.FINANCE_INDIVCREATE]: { cannotAssign: true },
    [CelKey.FINANCE_POCREATE]: { cannotAssign: true },

    [CelKey.LEADERSHIP_EDIT]: { cannotAssign: true },
    [CelKey.LEADERSHIP_EDIT_RESTRICTED]: { cannotAssign: true },

    [CelKey.MEMBERSHIP_INACTIVE_YOUTH]: { cannotAssign: true },
    [CelKey.MEMBERSHIP_INACTIVE_ADULT]: { cannotAssign: true },

    [CelKey.MESSAGING_CREATE]: { isUnitEnabled: true },
    [CelKey.MESSAGING_GROPU_CREATE]: { isUnitEnabled: true },

    [CelKey.UNIT_PROFILE_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_EDIT]: { cannotAssign: true },
    [CelKey.UNIT_REPORTS]: { cannotAssign: true },
    [CelKey.SUBUNIT_CREATE]: { cannotAssign: true },
    [CelKey.SUBUNIT_EDIT]: { cannotAssign: true },
    [CelKey.SUBUNIT_DENCHIEF_ADD]: { cannotAssign: true },
  },
};

export const getPermisionPositionByPanelType: (
  featurePermissionPositions: Record<
    string,
    {
      id: number;
      name: string;
    }
  >,
) => Record<string, number[]> = featurePermissionPositions => {
  const permsPositionsByPanel = {
    [PanelTitle.ACTIVITY]: [
      featurePermissionPositions[CelKey.ACTIVITY_CREATE].id,
    ],
    [PanelTitle.ADVANCEMENT]: [
      featurePermissionPositions[CelKey.ADVANCEMENT_AWARD].id,
      featurePermissionPositions[CelKey.ADVANCEMENT_APROVE].id,
      featurePermissionPositions[CelKey.ADVANCEMENT_EDIT].id,
      featurePermissionPositions[CelKey.ADVANCEMENT_MBLIST].id,
    ],
    [PanelTitle.ADVANCEMENT_YOUTH]: [
      featurePermissionPositions[CelKey.ADVANCEMENT_SCOUT_EDITOR].id,
      featurePermissionPositions[CelKey.ADVANCEMENT_TENDERFOOT_EDITOR].id,
      featurePermissionPositions[CelKey.ADVANCEMENT_SECONDCLASS_EDITOR].id,
      featurePermissionPositions[CelKey.ADVANCEMENT_FIRSTCLASS_EDITOR].id,
    ],
    [PanelTitle.CALENDAR]: [
      featurePermissionPositions[CelKey.CALENDAR_CREATE].id,
      featurePermissionPositions[CelKey.SUB_CALENDAR_CREATE].id,
    ],
    [PanelTitle.CALENDAR_YOUTH]: [
      featurePermissionPositions[CelKey.CALENDAR_CREATE].id,
      featurePermissionPositions[CelKey.SUB_CALENDAR_CREATE].id,
    ],
    [PanelTitle.FINANCE]: [
      featurePermissionPositions[CelKey.FINANCE_POCREATE].id,
    ],
    [PanelTitle.LEADERSHIP]: [],
    [PanelTitle.MEMBERSHIP]: [],
    [PanelTitle.MESSAGING]: [
      featurePermissionPositions[CelKey.MESSAGING_CREATE].id,
      featurePermissionPositions[CelKey.MESSAGING_GROPU_CREATE].id,
    ],
    [PanelTitle.MESSAGING_YOUTH]: [
      featurePermissionPositions[CelKey.MESSAGING_CREATE].id,
      featurePermissionPositions[CelKey.MESSAGING_GROPU_CREATE].id,
    ],
    [PanelTitle.UNIT]: [
      featurePermissionPositions[CelKey.UNIT_PROFILE_EDIT].id,
    ],
  };
  return permsPositionsByPanel;
};

export const getRequiredPermissionNames = (
  data: { position: string }[],
  level: 'unit' | 'subUnit',
): string[] =>
  data.reduce<string[]>((acc, pos) => {
    const posPermission: Record<string, PermsConfiguration> =
      PermsConfigurationObject[
        pos.position as keyof typeof PermsConfigurationObject
      ];

    if (isNil(posPermission)) {
      return acc;
    }

    const unitRequiredPerms = pickBy(
      posPermission,
      (value: PermsConfiguration) =>
        level === 'subUnit' ? value.isSubRequired : value.isUnitRequired,
    );

    const names = keys(unitRequiredPerms)
      .filter(
        key =>
          !(pos.position === YOUTH_MEMBER && key === CelKey.ADVANCEMENT_EDIT),
      )
      .map(key => DisplayPermissionPositions[key]?.name)
      .filter(Boolean) as string[];

    if (level === 'subUnit') {
      return names.map(name => {
        if (level === 'subUnit' && name === Positions.ACRUD) {
          return `${name} (sub-unit only)`;
        }

        return name;
      });
    }

    return acc.concat(names);
  }, []);
